import React, { useEffect, useMemo, useState } from "react";
import {
  CampaignRow,
  AdSetRow,
  AdRow,
  CreationTab,
  AdAssetFeedSpec,
  AdObjectStorySpec,
  AdCreative,
  AdCreativeFormat,
  CallToAction,
} from "@blisspointmedia/bpm-types/dist/MetaBuying";
import {
  SQUARE,
  HORIZONTAL,
  VERTICAL,
  IMAGE,
  SINGLE_IMAGE,
  SINGLE_VIDEO,
  DEFAULT_PLACEMENT_GROUPS,
  PLACEMENT_GROUP_OPTIONS,
} from "../../../MetaBuyingConstants";
import { Modal } from "react-bootstrap";
import { Button, ButtonType, Spinner } from "../../../../Components";
import "./ReviewModal.scss";
import { ButtonFrameworkVariant } from "../../../../Components/ButtonFramework";
import { MdCheck, MdClose, MdContentCopy, MdDelete, MdEdit } from "react-icons/md";
import { awaitJSON, MetaLambdaFetch } from "../../../../utils/fetch-utils";
import * as R from "ramda";
import { WHITE_META_LOGO } from "../../../MetaBuyingUtils";
import { formatMoney } from "../../../../utils/format-utils";
import { campaignFieldConfig, adSetFieldConfig, adFieldConfig } from "./ReviewModalConfigs";

export enum ReviewModalVariant {
  DRAFT = "draft",
  PUBLISHED = "published",
  EDITING = "editing",
}

export enum ReviewModalTab {
  NONE = "None",
  DETAILS = "Details",
  AD_PREVIEW = "Ad Preview",
}

const DimensionRows = ({
  tab,
  dimensions,
}: {
  tab: CreationTab;
  dimensions: Record<string, any>;
}): JSX.Element => (
  <>
    {R.toPairs(dimensions[tab]).map(([key, value]) => (
      <div className="fieldRow">
        <div className="field">{key}</div>
        <div className="value">{value}</div>
      </div>
    ))}
  </>
);

const ReviewModalBody = ({
  data,
  level,
  levelName,
  dimensions,
  width,
  variant = ReviewModalVariant.EDITING,
  fieldConfig = {},
}: {
  data: any;
  level: string;
  levelName: string;
  dimensions?: Record<string, any>;
  width?: number;
  variant?: ReviewModalVariant;
  fieldConfig?: Record<
    string,
    {
      label?: string | ((data: any) => string);
      formatter?: (value: any, data?: any) => string;
      condition?: (value: any) => boolean;
      skipIfEditing?: boolean;
    }
  >;
}): JSX.Element => {
  const formatValue = (key: string, value: any): string => {
    const config = fieldConfig[key];

    if (config?.formatter) {
      return config.formatter(value, data);
    }

    // Default formatters based on field name patterns
    if (key.includes("budget") || key.includes("spend") || key.includes("bid_amount")) {
      return formatMoney((value || 0) / 100);
    }

    if (key.includes("roas")) {
      return ((value || 0) / 10000).toFixed(3);
    }

    if (key === "created" || key === "updated") {
      return new Date(value).toLocaleString();
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    return value?.toString() || "";
  };

  const shouldShowField = (key: string, value: any): boolean => {
    const config = fieldConfig[key];

    // Skip if condition is not met
    if (config?.condition && !config.condition(value)) {
      return false;
    }

    // Skip if editing and field should be skipped
    if (variant === ReviewModalVariant.EDITING && config?.skipIfEditing) {
      return false;
    }

    // Skip if value is null, undefined, or empty string
    if (value === null || value === undefined || value === "") {
      return false;
    }

    return true;
  };

  const getFieldLabel = (key: string): string => {
    const config = fieldConfig[key];
    if (config?.label) {
      return typeof config.label === "function" ? config.label(data) : config.label;
    }

    // Convert snake_case to Title Case
    return key
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderField = (key: string, value: any): JSX.Element | null => {
    if (!shouldShowField(key, value)) {
      return null;
    }

    return (
      <div key={key} className="fieldRow">
        <div className="field">{getFieldLabel(key)}</div>
        <div className="value">{formatValue(key, value)}</div>
      </div>
    );
  };

  return (
    <div className="innerBody" style={width ? { width: `${width}%` } : {}}>
      <div className="headerRow">
        <div className={`level ${level}`}>{levelName}</div>
        <div className="name">{data[`${level}_name`] || data.name}</div>
      </div>

      {variant === "editing" && dimensions && dimensions[level as CreationTab] && (
        <DimensionRows tab={level as CreationTab} dimensions={dimensions} />
      )}

      {Object.keys(fieldConfig).map(key => renderField(key, data[key]))}
    </div>
  );
};

const CampaignView = ({
  campaign,
  dimensions,
  width,
  variant = ReviewModalVariant.EDITING,
}: {
  campaign: CampaignRow;
  dimensions?: Record<string, any>;
  width?: number;
  variant?: ReviewModalVariant;
}): JSX.Element => {
  return (
    <ReviewModalBody
      data={campaign}
      level="campaign"
      levelName="Campaign"
      dimensions={dimensions}
      width={width}
      variant={variant}
      fieldConfig={campaignFieldConfig(variant)}
    />
  );
};

const AdSetView = ({
  adset,
  dimensions,
  width,
  variant = ReviewModalVariant.EDITING,
}: {
  adset: AdSetRow;
  dimensions?: Record<string, any>;
  width?: number;
  variant?: ReviewModalVariant;
}): JSX.Element => {
  return (
    <ReviewModalBody
      data={adset}
      level="adset"
      levelName="Ad Set"
      dimensions={dimensions}
      width={width}
      variant={variant}
      fieldConfig={adSetFieldConfig(variant)}
    />
  );
};

const AdView = ({
  ad,
  dimensions,
  width,
  variant = ReviewModalVariant.EDITING,
}: {
  ad: AdRow;
  dimensions?: Record<string, any>;
  width?: number;
  variant?: ReviewModalVariant;
}): JSX.Element => {
  return (
    <ReviewModalBody
      data={ad}
      level="ad"
      levelName="Ad"
      dimensions={dimensions}
      width={width}
      variant={variant}
      fieldConfig={adFieldConfig(variant)}
    />
  );
};

export interface ReviewModalProps {
  variant: ReviewModalVariant;
  accountID: string;
  businessManager: string;
  isInternal?: boolean;
  campaign?: CampaignRow;
  adset?: AdSetRow;
  ad?: AdRow;
  dimensions?: Record<string, any>;
  escapable?: boolean;
  selectedTab: ReviewModalTab;
  setSelectedTab: React.Dispatch<React.SetStateAction<ReviewModalTab>>;
  onHide: () => void;
  publishAsPaused?: () => void;
  disablePublishAsPaused?: boolean;
  saveAsDraft?: () => void;
  edit?: () => void;
  approve?: () => void;
  disapprove?: () => void;
  remove?: () => void;
  loading?: boolean;
}

export const ReviewModal = ({
  variant,
  accountID,
  businessManager,
  isInternal = true,
  campaign,
  adset,
  ad,
  dimensions,
  escapable = true,
  selectedTab = ReviewModalTab.NONE,
  setSelectedTab = () => {},
  onHide = () => {},
  publishAsPaused = () => {},
  disablePublishAsPaused = false,
  saveAsDraft = () => {},
  edit = () => {},
  approve = () => {},
  disapprove = () => {},
  remove = () => {},
  loading = false,
}: ReviewModalProps): JSX.Element => {
  const isDraft = variant === ReviewModalVariant.DRAFT;
  const isPublished = variant === ReviewModalVariant.PUBLISHED;

  const modalTitle = useMemo(() => {
    let title = "";
    if ((isDraft || isPublished) && campaign) {
      title = `Review Campaign${isDraft ? " Draft" : ""}`;
    } else if ((isDraft || isPublished) && adset) {
      title = `Review Ad Set${isDraft ? " Draft" : ""}`;
    } else if ((isDraft || isPublished) && ad) {
      title = `Review Ad${isDraft ? " Draft" : ""}`;
    } else {
      title = "Review Draft";
    }

    return title;
  }, [ad, adset, campaign, isDraft, isPublished]);

  const { idTitle, idValue } = useMemo(() => {
    if (isInternal && campaign && !adset && !ad && isDraft) {
      return { idTitle: `Internal Campaign ID: ${campaign.id}`, idValue: campaign.id };
    } else if (campaign && !adset && !ad && isPublished && campaign.meta_id) {
      return { idTitle: `Meta Campaign ID: ${campaign.meta_id}`, idValue: campaign.meta_id };
    } else if (isInternal && !campaign && adset && !ad && isDraft) {
      return { idTitle: `Internal Ad Set ID: ${adset.id}`, idValue: adset.id };
    } else if (!campaign && adset && !ad && isPublished && adset.meta_id) {
      return { idTitle: `Meta Ad Set ID: ${adset.meta_id}`, idValue: adset.meta_id };
    } else if (isInternal && !campaign && !adset && ad && isDraft) {
      return { idTitle: `Internal Ad ID: ${ad.id}`, idValue: ad.id };
    } else if (!campaign && !adset && ad && isPublished && ad.meta_id) {
      return { idTitle: `Meta Ad ID: ${ad.meta_id}`, idValue: ad.meta_id };
    } else {
      return { idTitle: "", idValue: "" };
    }
  }, [ad, adset, campaign, isInternal, isDraft, isPublished]);

  const [iframeIsLoaded, setIframeIsLoaded] = useState(false);
  const [iframeFields, setIframeFields] = useState<{
    src: string;
    width: string;
    height: string;
  }>();

  const formattedAdCreative = useMemo(() => {
    if (!ad) {
      return null;
    }

    const creativeFormat = ad.format;
    const creativeAssetSquare = ad.image_hash_square;
    const creativeAssetHorizontal = ad.image_hash_horizontal;

    let defaultAsset = creativeAssetSquare
      ? SQUARE
      : creativeAssetHorizontal
      ? HORIZONTAL
      : VERTICAL;

    let asset_feed_spec: AdAssetFeedSpec = {
      ad_formats: [creativeFormat === AdCreativeFormat[IMAGE] ? SINGLE_IMAGE : SINGLE_VIDEO],
      bodies: [
        {
          text: ad.message || "",
        },
      ],
      titles: [
        {
          text: ad.headline || "",
        },
      ],
      call_to_action_types: [ad.call_to_action as CallToAction],
      descriptions: [{ text: ad.description || "" }],
      link_urls: [{ website_url: ad.link || "" }],
      asset_customization_rules: [HORIZONTAL, VERTICAL].reduce(
        (acc, frame) => {
          const creativeAsset = ad[`creative_asset_${frame}`];
          if (defaultAsset !== frame && creativeAsset) {
            acc.push({
              [`${creativeFormat === AdCreativeFormat.IMAGE ? "image" : "video"}_label`]: {
                name: `asset_${frame}`,
              },
              customization_spec: DEFAULT_PLACEMENT_GROUPS[frame],
            });
          }
          return acc;
        },
        [
          {
            [`${creativeFormat === AdCreativeFormat.IMAGE ? "image" : "video"}_label`]: {
              name: `asset_${defaultAsset}`,
            },
            customization_spec: {},
          },
        ]
      ),
    };

    let assets = PLACEMENT_GROUP_OPTIONS.reduce((acc, { value }) => {
      const creativeAsset = ad[`creative_asset_${value}`];
      if (creativeAsset) {
        acc.push({
          [creativeFormat === AdCreativeFormat.IMAGE ? "hash" : "video_id"]: creativeAsset[
            creativeFormat === AdCreativeFormat.IMAGE ? "hash" : "video_id"
          ],
          adlabels: [{ name: `asset_${value}` }],
        });
      }
      return acc;
    }, [] as any[]);
    asset_feed_spec[creativeFormat === AdCreativeFormat.IMAGE ? "images" : "videos"] = assets;

    let object_story_spec: AdObjectStorySpec = {
      page_id: ad.page_id,
    };

    let creativeSpec: AdCreative = {
      object_story_spec,
      asset_feed_spec,
    };

    return creativeSpec;
  }, [ad]);

  useEffect(() => {
    if (!iframeFields && ad) {
      (async () => {
        try {
          const res = await MetaLambdaFetch("/getAdPreview", {
            method: "POST",
            body: {
              accountID,
              businessManager,
              adMetaId: ad.meta_id,
              adCreative: formattedAdCreative,
            },
          });
          const parsedRes = await awaitJSON(res);
          setIframeFields({
            src: parsedRes.src,
            width: parsedRes.width,
            height: parsedRes.height,
          });
        } catch (e) {}
      })();
    }
  }, [iframeFields, setIframeFields, accountID, businessManager, ad, formattedAdCreative]);

  const columnWidth = useMemo(() => 100 / ((campaign ? 1 : 0) + (adset ? 1 : 0) + (ad ? 1 : 0)), [
    campaign,
    adset,
    ad,
  ]);

  return (
    <Modal
      className={`reviewModal ${variant}`}
      show={true}
      onHide={onHide}
      centered
      backdrop={escapable ? true : "static"}
      keyboard={escapable}
    >
      <Modal.Header closeButton className="reviewModalHeader">
        {((isInternal && isDraft) || isPublished) && (
          <div className="titleInfo">
            <div className="modalTitle">{modalTitle}</div>
            <div className="modalID">
              <div className="idDisplay">{idTitle}</div>
              <Button
                icon={<MdContentCopy />}
                variant={ButtonFrameworkVariant.ICON_ONLY}
                type={ButtonType.EMPTY}
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(idValue);
                }}
              />
            </div>
          </div>
        )}
        {((!isInternal && isDraft) || variant === ReviewModalVariant.EDITING) && (
          <div className="modalTitle">{modalTitle}</div>
        )}
        {
          // Only show tabs if there is an ad
          ad && (
            <div className="titleButtons">
              <Button
                className={`titleButton${selectedTab === "Details" ? " selected" : " unselected"}`}
                type={ButtonType.EMPTY}
                onClick={() => {
                  setSelectedTab(ReviewModalTab.DETAILS);
                  setIframeIsLoaded(false);
                }}
              >
                Details
              </Button>
              <Button
                className={`titleButton${
                  selectedTab === "Ad Preview" ? " selected" : " unselected"
                }`}
                type={ButtonType.EMPTY}
                onClick={() => {
                  setSelectedTab(ReviewModalTab.AD_PREVIEW);
                }}
              >
                Ad Preview
              </Button>
            </div>
          )
        }
      </Modal.Header>
      <Modal.Body className="reviewModalBody">
        {campaign && selectedTab !== ReviewModalTab.AD_PREVIEW && (
          <CampaignView
            campaign={campaign}
            dimensions={dimensions}
            variant={variant}
            width={columnWidth}
          />
        )}
        {adset && selectedTab !== ReviewModalTab.AD_PREVIEW && (
          <AdSetView adset={adset} dimensions={dimensions} width={columnWidth} variant={variant} />
        )}
        {ad && selectedTab !== ReviewModalTab.AD_PREVIEW && (
          <AdView ad={ad} dimensions={dimensions} width={columnWidth} variant={variant} />
        )}
        {ad && selectedTab === ReviewModalTab.AD_PREVIEW && (
          <div className="adPreview">
            {!iframeIsLoaded && <Spinner />}
            <div
              className="mobilePreview"
              style={{
                opacity: iframeIsLoaded ? 1 : 0,
                height: iframeIsLoaded ? "auto" : 0,
                width: iframeIsLoaded ? "auto" : 0,
                transition: "opacity 0.3s ease-in-out",
              }}
            >
              {iframeFields && (
                <iframe
                  title="test"
                  onLoad={() => setIframeIsLoaded(true)}
                  src={iframeFields?.src}
                  width={iframeFields?.width}
                  height={iframeFields?.height}
                  scrolling="yes"
                  style={{
                    border: "none",
                    display: "block",
                    borderRadius: "20px",
                    visibility: iframeIsLoaded ? "visible" : "hidden",
                  }}
                ></iframe>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      {isPublished && (
        <Modal.Footer className="reviewModalFooter warning">
          Details may not be correct here if edits have been made in Ads Manager
        </Modal.Footer>
      )}
      {!isInternal && isDraft && (
        <Modal.Footer className="reviewModalFooter">
          <div className="mainActions">
            <Button
              className="disapproveButton"
              type={ButtonType.OUTLINED}
              variant={ButtonFrameworkVariant.TRAILING_ICON}
              icon={<MdClose />}
              onClick={disapprove}
              enableAnimation={false}
              disabled={ad && ad.approval_stage !== "IN_REVIEW"}
            >
              Disapprove
            </Button>
            <Button
              className="approveButton"
              type={ButtonType.OUTLINED}
              variant={ButtonFrameworkVariant.TRAILING_ICON}
              icon={<MdCheck />}
              onClick={approve}
              enableAnimation={false}
              disabled={ad && ad.approval_stage !== "IN_REVIEW"}
            >
              Approve
            </Button>
          </div>
        </Modal.Footer>
      )}
      {((isInternal && isDraft) || variant === ReviewModalVariant.EDITING) && (
        <Modal.Footer className="reviewModalFooter">
          <div className="deleteAction">
            <Button
              variant={ButtonFrameworkVariant.ICON_ONLY}
              type={ButtonType.EMPTY}
              icon={<MdDelete />}
              onClick={remove}
              disabled={loading}
            />
          </div>
          <div className="mainActions">
            <Button
              className="editButton"
              variant={ButtonFrameworkVariant.LEADING_ICON}
              icon={<MdEdit />}
              type={ButtonType.OUTLINED}
              onClick={edit}
              disabled={loading || (isDraft && !ad)}
            >
              Edit
            </Button>
            <Button
              variant={ButtonFrameworkVariant.TRAILING_ICON}
              icon={
                <img src={WHITE_META_LOGO} alt="icon" style={{ width: "22px", height: "22px" }} />
              }
              type={ButtonType.FILLED}
              onClick={publishAsPaused}
              disabled={
                loading || disablePublishAsPaused // TODO: Allow publish as paused if not approved during user testing.
              }
            >
              Publish As Paused
            </Button>
            {variant === ReviewModalVariant.EDITING && (
              <Button type={ButtonType.FILLED} onClick={saveAsDraft} disabled={loading}>
                Save as Draft
              </Button>
            )}
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ReviewModal;
